import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Message, User } from '../types';
import { getMockMessages, getMockUsers } from '../data/mockData';
import { Search, MessageSquare, Plus } from 'lucide-react';
import ConversationView from '../components/messaging/ConversationView';

const Messages = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showNewConversation, setShowNewConversation] = useState(false);

  useEffect(() => {
    document.title = 'Messages - FemSportNet';
    
    const fetchData = async () => {
      try {
        setIsLoading(true);
        if (currentUser) {
          const [usersData, messagesData] = await Promise.all([
            getMockUsers(),
            getMockMessages(currentUser.id)
          ]);
          
          const filteredUsers = usersData.filter(u => u.id !== currentUser.id);
          setUsers(filteredUsers);
          setMessages(messagesData);
          
          if (messagesData.length > 0) {
            const firstConversationUserId = messagesData[0].senderId === currentUser.id 
              ? messagesData[0].recipientId 
              : messagesData[0].senderId;
            setSelectedUserId(firstConversationUserId);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const getConversations = () => {
    if (!currentUser) return [];
    
    const userIds = new Set<string>();
    messages.forEach(msg => {
      if (msg.senderId === currentUser.id) {
        userIds.add(msg.recipientId);
      } else if (msg.recipientId === currentUser.id) {
        userIds.add(msg.senderId);
      }
    });
    
    return Array.from(userIds).map(userId => {
      const user = users.find(u => u.id === userId);
      const userMessages = messages.filter(
        msg => (msg.senderId === userId && msg.recipientId === currentUser.id) || 
               (msg.senderId === currentUser.id && msg.recipientId === userId)
      ).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      
      const lastMessage = userMessages[userMessages.length - 1];
      const unreadCount = userMessages.filter(
        msg => msg.senderId === userId && msg.recipientId === currentUser.id && !msg.read
      ).length;
      
      return {
        userId,
        user,
        lastMessage,
        unreadCount
      };
    });
  };

  const getFilteredConversations = () => {
    const conversations = getConversations();
    if (!searchTerm) return conversations;
    
    return conversations.filter(convo => 
      convo.user?.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getCurrentConversation = () => {
    if (!currentUser || !selectedUserId) return [];
    
    return messages
      .filter(msg => 
        (msg.senderId === currentUser.id && msg.recipientId === selectedUserId) || 
        (msg.senderId === selectedUserId && msg.recipientId === currentUser.id)
      )
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  };

  const getSelectedUser = () => {
    return users.find(u => u.id === selectedUserId);
  };

  const handleSendMessage = async (content: string, attachments?: File[]) => {
    if (!content.trim() || !currentUser || !selectedUserId) return;
    
    const newMsg: Message = {
      id: Math.random().toString(36).substring(2, 15),
      senderId: currentUser.id,
      recipientId: selectedUserId,
      content,
      read: false,
      createdAt: new Date().toISOString()
    };
    
    setMessages([...messages, newMsg]);
    
    if (attachments && attachments.length > 0) {
      console.log('Processing attachments:', attachments);
    }
  };

  const startNewConversation = (userId: string) => {
    setSelectedUserId(userId);
    setShowNewConversation(false);
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-6 transition-colors duration-200">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden transition-colors duration-200">
          <div className="grid grid-cols-1 md:grid-cols-3 h-[calc(100vh-120px)]">
            
            {/* Conversations List */}
            <div className="md:col-span-1 border-r border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Messages</h2>
                <div className="mt-2 relative">
                  <input
                    type="text"
                    placeholder="Search conversations"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full py-2 pl-10 pr-4 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                  />
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                </div>
              </div>
              
              <div className="overflow-y-auto h-[calc(100%-120px)]">
                {isLoading ? (
                  <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
                  </div>
                ) : (
                  getFilteredConversations().length > 0 ? (
                    getFilteredConversations().map(convo => (
                      <div
                        key={convo.userId}
                        onClick={() => setSelectedUserId(convo.userId)}
                        className={`flex items-center p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          selectedUserId === convo.userId ? 'bg-gray-100 dark:bg-gray-700' : ''
                        } transition-colors duration-200`}
                      >
                        <div className="relative">
                          <div className="w-12 h-12 rounded-full overflow-hidden">
                            <img 
                              src={convo.user?.avatar} 
                              alt={convo.user?.name} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {convo.unreadCount > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-xs text-white">{convo.unreadCount}</span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4 flex-grow">
                          <div className="flex justify-between items-center">
                            <h3 className="font-medium text-gray-900 dark:text-white">{convo.user?.name}</h3>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {convo.lastMessage ? formatMessageTime(convo.lastMessage.createdAt) : ''}
                            </span>
                          </div>
                          {convo.lastMessage && (
                            <p className={`text-sm truncate ${
                              convo.unreadCount > 0 
                                ? 'font-medium text-gray-900 dark:text-white' 
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {convo.lastMessage.senderId === currentUser?.id ? 'You: ' : ''}
                              {convo.lastMessage.content}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                      <p className="text-gray-500 dark:text-gray-400 mb-2">No se encontraron conversaciones</p>
                      {searchTerm && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => setSearchTerm('')}
                            className="text-green-500 hover:text-green-600 text-sm"
                          >
                            Limpiar búsqueda
                          </button>
                          <button
                            onClick={() => setShowNewConversation(!showNewConversation)}
                            className="flex items-center gap-1 text-green-500 hover:text-green-600 text-sm"
                          >
                            <Plus size={16} /> Nueva conversación
                          </button>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>

            {/* New Conversation */}
            {showNewConversation && (
              <div className="border-b border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto">
                <div className="p-3 bg-gray-50 dark:bg-gray-700">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Iniciar nueva conversación</h3>
                  {users.filter(u => !getConversations().some(c => c.userId === u.id)).map(user => (
                    <button
                      key={user.id}
                      onClick={() => startNewConversation(user.id)}
                      className="w-full flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full overflow-hidden mr-3">
                        <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user.sport}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Chat Area */}
            <div className="md:col-span-2 flex flex-col">
              {selectedUserId ? (
                <ConversationView
                  selectedUser={getSelectedUser()!}
                  messages={getCurrentConversation()}
                  onSendMessage={handleSendMessage}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                    <MessageSquare className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Tus Mensajes</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-center max-w-xs mb-4">
                    Conecta con otras atletas, entrenadoras y profesionales del deporte
                  </p>
                  <button 
                    onClick={() => setShowNewConversation(true)}
                    className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 py-2 rounded-md transition-colors duration-200"
                  >
                    Iniciar Nueva Conversación
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
